import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonAddressBooks,
  CommonScrollIntoView,
  CommonRequired,
  CommonOnError,
  CommonOnUpdate,
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import {
  CountryArrayModel, CountryModel, DateModel, LocationModel, NameModel
} from 'forms/shared/types';
import { Dispatch } from 'redux';
import { SpouseForeignContactsItemModel } from 'forms/sections/foreign/contacts/types';
import Checkbox from 'components/Form/Checkbox';
import Field from 'components/Form/Field';
import Name from 'components/Form/Name';
import NotApplicable from 'components/Form/NotApplicable';
import Textarea from 'components/Form/Textarea';
import DateControl from 'components/Form/DateControl';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import BranchCollection from 'components/Form/BranchCollection';
import Country from 'components/Form/Country';
import Location from 'components/Form/Location';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import PVQName from 'components/Form/PVQName';
import { otherNamesOptions } from 'constants/enums/relationshipOptions';
import PVQLocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import {
  foreignContactFrequencyOptions, foreignContactRelationshipOptions,
  foreignNationalWorkOptions
} from 'constants/enums/foreignActivityOptions';
import Branch from 'components/Form/Branch';
import { DeepSafePartial } from 'types/types';
import { isSafeInternational } from 'helpers/locations';
import { ForeignContactsItemAliasesModel } from '../../../../forms/sections/foreign/contacts/types';

type UpdateField<Model extends {}> = <Key extends keyof Model>(
  field: Key,
  values: Model[Key]
) => void

interface Props extends Partial<SpouseForeignContactsItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  applicantBirthdate: DateModel
  dispatch: Dispatch
  formType?: string
}

export default class SpouseForeignNational extends ValidationElement<Props> {
  constructor(props) {
    super(props);

    this.state = {
      currentAddressIdkRefresh: 0,
    };
  }

  handleNameError = (value: any, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<SpouseForeignContactsItemModel>) => {
    this.props.onUpdate({
      ListedPreviously: this.props.ListedPreviously,
      Name: this.props.Name,
      LastContact: this.props.LastContact,
      Frequency: this.props.Frequency,
      FrequencyExplanation: this.props.FrequencyExplanation,
      Relationship: this.props.Relationship,
      RelationshipExplanation: this.props.RelationshipExplanation,
      SpouseRelationship: this.props.SpouseRelationship,
      SpouseRelationshipExplanation: this.props.SpouseRelationshipExplanation,
      Aliases: this.props.Aliases,
      Citizenship: this.props.Citizenship,
      CitizenshipNotApplicable: this.props.CitizenshipNotApplicable,
      Birthdate: this.props.Birthdate,
      BirthdateNotApplicable: this.props.BirthdateNotApplicable,
      Birthplace: this.props.Birthplace,
      BirthplaceNotApplicable: this.props.BirthplaceNotApplicable,
      Address: this.props.Address,
      AddressNotApplicable: this.props.AddressNotApplicable,
      InUsaNotApplicable: this.props.InUsaNotApplicable,
      ForeignDisciplines: this.props.ForeignDisciplines,
      ForeignDisciplinesOtherExplanation: this.props.ForeignDisciplinesOtherExplanation,
      ForeignDisciplinesNotApplicable: this.props.ForeignDisciplinesNotApplicable,
      ForeignAssociationDescription: this.props.ForeignAssociationDescription,
      ForeignAssociationDescriptionNotApplicable: this.props.ForeignAssociationDescriptionNotApplicable,
      Obligations: this.props.Obligations,
      ...queue,
    });
  };

  updateField: UpdateField<SpouseForeignContactsItemModel> = (field, values) => {
    this.update({
      [field]: values,
    });
  };

  updateFrequency = (values: SpouseForeignContactsItemModel['Frequency']) => {
    this.update({
      Frequency: values,
      FrequencyExplanation: values.value === 'Other' ? this.props.FrequencyExplanation : {}
    });
  };

  updateName = (value: SpouseForeignContactsItemModel['Name']) => {
    this.update({
      Name: value,
    });
  };

  updateCitizenship = (value: SpouseForeignContactsItemModel['Citizenship']) => {
    this.update({
      Citizenship: value,
    });
  };

  updateCitizenshipNotApplicable = (value: SpouseForeignContactsItemModel['CitizenshipNotApplicable']) => {
    this.update({
      Citizenship: value.applicable ? this.props.Citizenship : { },
      CitizenshipNotApplicable: value,
    });
  };

  updateBirthdate = (value: SpouseForeignContactsItemModel['Birthdate']) => {
    this.update({
      Birthdate: value,
    });
  };

  updateBirthdateNotApplicable = (value: SpouseForeignContactsItemModel['BirthdateNotApplicable']) => {
    this.update({
      Birthdate: value.applicable ? this.props.Birthdate : { month: '', day: '', year: '' },
      BirthdateNotApplicable: value,
    });
  };

  updateBirthplace = (value: SpouseForeignContactsItemModel['Birthplace']) => {
    this.update({
      Birthplace: value,
    });
  };

  updateBirthplaceNotApplicable = (value: SpouseForeignContactsItemModel['BirthplaceNotApplicable']) => {
    this.update({
      Birthplace: value.applicable ? this.props.Birthplace : {},
      BirthplaceNotApplicable: value,
      BirthplaceExplanation: {},
    });
  };

  updateRelationship = (value: SpouseForeignContactsItemModel['Relationship']) => {
    this.update({
      Relationship: value,
      RelationshipExplanation: this.props.Relationship === 'Other' ? this.props.RelationshipExplanation : {}
    });
  };

  updateRelationshipExplanation = (value: SpouseForeignContactsItemModel['RelationshipExplanation']) => {
    this.update({
      RelationshipExplanation: value
    });
  };

  updateSpouseRelationship = (value: SpouseForeignContactsItemModel['SpouseRelationship']) => {
    this.update({
      SpouseRelationship: value,
      SpouseRelationshipExplanation: this.props.SpouseRelationship === 'Other' ? this.props.SpouseRelationshipExplanation : {}
    });
  };

  updateSpouseRelationshipExplanation = (value: SpouseForeignContactsItemModel['SpouseRelationshipExplanation']) => {
    this.update({
      SpouseRelationshipExplanation: value
    });
  };

  updateAddress = (
    values: SpouseForeignContactsItemModel['Address']
  ) => {
    this.update({ Address: values });
  };

  updateAddressNotApplicable = (
    addressNotApplicable: SpouseForeignContactsItemModel['AddressNotApplicable']
  ) => {
    const queue: Partial<SpouseForeignContactsItemModel> = { AddressNotApplicable: addressNotApplicable };

    if (addressNotApplicable?.applicable === false) {
      const address: LocationModel | undefined = this.props.Address;

      let country: CountryModel | string | CountryArrayModel | undefined = address?.country;

      if (country) {
        if (isSafeInternational(address)) {
          country = { value: 'INTERNATIONAL' } as CountryModel;
        }

        queue.Address = { country } as LocationModel;
      } else {
        queue.Address = {} as LocationModel;
      }
    }

    this.update(queue);
  };

  updateInUsaNotApplicable = (
    inUsaNotApplicable: SpouseForeignContactsItemModel['InUsaNotApplicable']
  ) => {
    const queue: Partial<SpouseForeignContactsItemModel> = {
      InUsaNotApplicable: { applicable: inUsaNotApplicable?.applicable }
    };

    if (!inUsaNotApplicable?.applicable) {
      queue.Address = {} as LocationModel;
    }

    this.update(queue);
  };

  updateForeignDisciplines = (values: SpouseForeignContactsItemModel['ForeignDisciplines']) => {
    const list = Checkbox.select(values, this.props.ForeignDisciplines);
    const includesOther = list.includes('Other Government Agency');
    const includesNone = list.includes('They are not associated with any of the above.');

    if (includesNone) {
      this.update({
        ForeignDisciplines: { values: Checkbox.select(values, {}) },
        ForeignDisciplinesOtherExplanation: {},
      });
    } else {
      this.update({
        ForeignDisciplines: { values: list },
        ForeignDisciplinesOtherExplanation: includesOther ? this.props.ForeignDisciplinesOtherExplanation : {},
      });
    }
  };

  updateForeignDisciplinesNotApplicable = (value: SpouseForeignContactsItemModel['ForeignDisciplinesNotApplicable']) => {
    this.update({
      ForeignDisciplines: value?.applicable ? this.props.ForeignDisciplines : {},
      ForeignDisciplinesNotApplicable: value,
    });
  };

  updateForeignDisciplinesOtherExplanation = (value: SpouseForeignContactsItemModel['ForeignDisciplinesOtherExplanation']) => {
    this.update({
      ForeignDisciplinesOtherExplanation: value
    });
  };

  updateForeignAssociationDescription = (value: SpouseForeignContactsItemModel['ForeignAssociationDescription']) => {
    this.update({
      ForeignAssociationDescription: value,
      ForeignAssociationDescriptionNotApplicable: { applicable: true }
    });
  };

  updateForeignAssociationDescriptionNotApplicable = (value: SpouseForeignContactsItemModel['ForeignAssociationDescriptionNotApplicable']) => {
    this.update({
      ForeignAssociationDescription: value?.applicable ? this.props.ForeignAssociationDescription : {},
      ForeignAssociationDescriptionNotApplicable: value,
    });
  };

  updateObligations = (
    values: SpouseForeignContactsItemModel['Obligations']
  ) => {
    this.update({ Obligations: values });
  };

  updateListedPreviously = (values) => {
    let additionalValues = {};

    if (values?.value === 'Yes') {
      additionalValues = {
        Name: {},
        LastContact: {},
        Frequency: {},
        FrequencyExplanation: {},
        Relationship: {},
        RelationshipExplanation: {},
        SpouseRelationship: {},
        SpouseRelationshipExplanation: {},
        Aliases: {},
        Citizenship: {},
        Birthdate: {},
        BirthdateNotApplicable: {},
        Birthplace: {},
        BirthplaceNotApplicable: {},
        Address: {},
        AddressNotApplicable: {},
        ForeignDisciplines: {},
        ForeignDisciplinesOtherExplanation: {},
        ForeignDisciplinesNotApplicable: {},
        ForeignAssociationDescription: {},
        ForeignAssociationDescriptionNotApplicable: {},
        Obligations: {},
      };
    }
    this.update({
      ListedPreviously: values,
      ...additionalValues
    });
  };

  render() {
    const attribute = `SpouseForeignContactsList.items[${this.props.$index}].Item`;

    const birthplaceAddress:DeepSafePartial<LocationModel> = this.props.Birthplace ?? {};
    if (!birthplaceAddress?.country) {
      birthplaceAddress.country = { value: 'INTERNATIONAL' };
    }

    return (
      <div className="foreign-national">
        <Branch
          {...this.props.ListedPreviously}
          name="ListedPreviously"
          attribute={`${attribute}.ListedPreviously`}
          label={i18n.t('foreign.pvq.spouseContacts.heading.listedPreviously')}
          labelSize="h4"
          className="listed-previously"
          warning={false}
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateListedPreviously}
          scrollIntoView={this.props.scrollIntoView}
        />

        {this.props.ListedPreviously?.value === 'No' && (
        <>
          <Field
            title={i18n.t('foreign.pvq.spouseContacts.heading.name')}
            optional
            filterErrors={Name.requiredErrorsOnly}
            className="foreign-national-name"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Name`}
          >
            <PVQName
              {...this.props.Name}
              name="Name"
              onUpdate={this.updateName}
              aria-label="Provide the name of the individual involved in the contact"
              onError={this.handleNameError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Name`}
              showFirstUnknown
              showMiddleUnknown
              showLastUnknown
            />
          </Field>

          <BranchCollection
            {...this.props.Aliases}
            label={i18n.t('foreign.pvq.spouseContacts.heading.aliases')}
            appendLabel={i18n.t('foreign.pvq.spouseContacts.heading.aliases2')}
            help="foreign.pvq.spouseContacts.help.aliases"
            className="aliases"
            onUpdate={(value: SpouseForeignContactsItemModel['Aliases']) => {
              this.updateField('Aliases', value);
            }}
            required={this.props.required}
            onError={this.props.onError}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Aliases`}
            showDNK
          >
            <ForeignNationalNameItem
              bind
              name="Item"
              handleNameError={this.handleNameError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.Aliases`}
              formType={this.props.formType}
            />
          </BranchCollection>

          <Field
            title={i18n.t('foreign.pvq.spouseContacts.heading.citizenship')}
            help="foreign.pvq.spouseContacts.help.citizenship"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Citizenship`}
          >
            <NotApplicable
              {...this.props.CitizenshipNotApplicable}
              name="CitizenshipNotApplicable"
              className="na-citizenship"
              label={i18n.t('foreign.pvq.spouseContacts.label.idk')}
              or={i18n.t('foreign.pvq.spouseContacts.para.or')}
              onUpdate={this.updateCitizenshipNotApplicable}
              required={this.props.required}
              onError={this.props.onError}
              attribute={`${attribute}.CitizenshipNotApplicable`}
            >
              <Country
                {...this.props.Citizenship}
                name="Citizenship"
                multiple
                className="citizenship"
                onUpdate={this.updateCitizenship}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.Citizenship`}
                excludeUnitedStates
              />
            </NotApplicable>
          </Field>

          <Field
            title={i18n.t('foreign.pvq.spouseContacts.heading.birthdate')}
            adjustFor="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Birthdate`}
          >
            <NotApplicable
              {...this.props.BirthdateNotApplicable}
              name="BirthdateNotApplicable"
              className="na-birthdate"
              label={i18n.t('foreign.pvq.spouseContacts.label.idk')}
              or={i18n.t('foreign.pvq.spouseContacts.para.or')}
              onUpdate={this.updateBirthdateNotApplicable}
              required={this.props.required}
              onError={this.props.onError}
              attribute={`${attribute}.BirthdateNotApplicable`}
            >
              <DateControl
                {...this.props.Birthdate}
                name="Birthdate"
                prefix="foreignBirthdate"
                relationship="Other"
                onUpdate={this.updateBirthdate}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.Birthdate`}
              />
            </NotApplicable>
          </Field>

          <Field
            adjustFor="address"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Birthplace`}
          >
            <PVQLocationUnknown
              {...birthplaceAddress}
              addressUnknown={this.props.BirthplaceNotApplicable}
              addressUnknownLabel={i18n.t('foreign.pvq.spouseContacts.label.idk')}
              onUpdateAddressUnknown={this.updateBirthplaceNotApplicable}
              name="Birthplace"
              className="birthplace"
              layout={Location.ADDRESS}
              addressBooks={this.props.addressBooks}
              addressBook="ForeignNational"
              dispatch={this.props.dispatch}
              onUpdate={this.updateBirthplace}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Birthplace`}
              excludeUnitedStates
              showAddressUnknown
              showCityUnknown
              disableToggle
              isPoBoxAllowed={false}
              showCountryNotApplicable
              label={i18n.t('foreign.pvq.spouseContacts.heading.birthplace')}
              domesticTitle={i18n.t('foreign.pvq.spouseContacts.heading.birthplace')}
              foreignTitle={i18n.t('foreign.pvq.spouseContacts.heading.birthplace')}
            />
          </Field>

          <Field
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Address`}
            adjustFor="address"
          >
            <PVQLocationUnknown
              {...this.props.Address}
              addressUnknown={this.props.AddressNotApplicable}
              addressUnknownLabel={i18n.t('foreign.pvq.contacts.label.idk')}
              attribute={`${attribute}.Address`}
              name="foreign-national-address"
              onUpdate={this.updateAddress} // eslint-disable-line @typescript-eslint/unbound-method
              onUpdateAddressUnknown={this.updateAddressNotApplicable}
              onUpdateInUsaUnknown={this.updateInUsaNotApplicable}
              inUsaUnknown={this.props.InUsaNotApplicable}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              onError={this.props.onError}
              required={this.props.required}
              geocode
              showInUsaUnknown
              showAddressUnknown
              showCityUnknown
              showCountryNotApplicable
              hideInstallationNameUnknownWhenForeign
              showInternationalStreet
              showIsInstallationUnknownIfDomestic
              showIsInstallationUnknownIfInternational
              showMilitaryInstallation
              showStateUnknown
              showStreetUnknown
              showZipcodeUnknown
              showApoFpoUnknown
              label={i18n.t(`foreign.pvq.spouseContacts.heading.address`)}
              domesticTitle={i18n.t(`foreign.pvq.spouseContacts.heading.addressDomesticTitle`)}
              foreignTitle={i18n.t(`foreign.pvq.spouseContacts.heading.addressForeignTitle`)}
            />
          </Field>

          <Field
            title={i18n.t('foreign.pvq.spouseContacts.heading.spouseRelationship')}
            className="spouse-relationship"
            adjustFor="p"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.SpouseRelationship`}
            exactAttribute
          >
            <RadioGroup
              className="spouse-relationship option-list option-list-vertical"
              required={this.props.required}
              onError={this.props.onError}
              selectedValue={this.props.SpouseRelationship?.value}
              attribute={`${attribute}.SpouseRelationship`}
            >
              {foreignContactRelationshipOptions.map((option) => (
                <Radio
                  key={option}
                  name={option}
                  label={option}
                  value={option}
                  className={option}
                  onUpdate={this.updateSpouseRelationship}
                  onError={this.props.onError}
                />
              ))}
            </RadioGroup>
          </Field>

            {this.props.SpouseRelationship?.value === 'Other'
              && (
                <Field
                  title={i18n.t('foreign.pvq.spouseContacts.heading.explanation')}
                  titleSize="label"
                  adjustFor="textarea"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.SpouseRelationshipExplanation`}
                >
                  <Textarea
                    {...this.props.SpouseRelationshipExplanation}
                    name="SpouseRelationshipExplanation"
                    className="spouse-relationship-explanation"
                    onUpdate={this.updateSpouseRelationshipExplanation}
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.SpouseRelationshipExplanation`}
                  />
                </Field>
              )}

          <Field
            title={i18n.t('foreign.pvq.spouseContacts.heading.relationship')}
            className="relationship"
            adjustFor="p"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Relationship`}
          >
            <RadioGroup
              className="relationship option-list option-list-vertical"
              required={this.props.required}
              onError={this.props.onError}
              selectedValue={this.props.Relationship?.value}
              attribute={`${attribute}.Relationship`}
            >
              {foreignContactRelationshipOptions.map((option) => (
                <Radio
                  key={option}
                  name={option}
                  label={option}
                  value={option}
                  className={option}
                  onUpdate={this.updateRelationship}
                  onError={this.props.onError}
                />
              ))}
            </RadioGroup>
          </Field>

          {this.props.Relationship?.value === 'Other'
          && (
          <Field
            title={i18n.t('foreign.pvq.spouseContacts.heading.explanation')}
            titleSize="label"
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.RelationshipExplanation`}
          >
            <Textarea
              {...this.props.RelationshipExplanation}
              name="RelationshipExplanation"
              className="relationship-explanation"
              onUpdate={this.updateRelationshipExplanation}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.RelationshipExplanation`}
            />
          </Field>
          )}

          <Field
            title={i18n.t('foreign.pvq.spouseContacts.heading.lastcontact')}
            help="foreign.spouseContacts.help.lastcontact"
            adjustFor="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.LastContact`}
          >
            <DateControl
              {...this.props.LastContact}
              name="LastContact"
              className="last-contact"
              prefix="contact.last"
              minDate={this.props.FirstContact}
              minDateEqualTo
              onUpdate={(value: SpouseForeignContactsItemModel['LastContact']) => {
                this.updateField('LastContact', value);
              }}
              onError={this.props.onError}
              required={this.props.required}
              hideDay
              attribute={`${attribute}.LastContact`}
            />
          </Field>

          <Field
            title={i18n.t('foreign.pvq.spouseContacts.heading.frequency')}
            className="foreign-contact-frequency"
            adjustFor="big-buttons"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Frequency`}
          >
            <RadioGroup
              className="frequency option-list option-list-vertical"
              required={this.props.required}
              onError={this.props.onError}
              selectedValue={(this.props.Frequency || {}).value}
              attribute={`${attribute}.Frequency`}
            >
              {foreignContactFrequencyOptions.map((option) => (
                <Radio
                  key={option}
                  name={option}
                  label={option}
                  value={option}
                  className={option}
                  onUpdate={this.updateFrequency}
                  onError={this.props.onError}
                />
              ))}
            </RadioGroup>
          </Field>

          {this.props.Frequency?.value === 'Other'
          && (
          <Field
            title={i18n.t('foreign.pvq.spouseContacts.heading.explanation')}
            titleSize="label"
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.FrequencyExplanation`}
          >
            <Textarea
              {...this.props.FrequencyExplanation}
              name="FrequencyExplanation"
              className="frequency-explanation"
              onUpdate={(value: SpouseForeignContactsItemModel['FrequencyExplanation']) => {
                this.updateField('FrequencyExplanation', value);
              }}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.FrequencyExplanation`}
            />
          </Field>
          )}

          <Field
            title={i18n.t('foreign.pvq.spouseContacts.heading.foreignDisciplines')}
            adjustFor="buttons"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ForeignDisciplines`}
          >
            <NotApplicable
              {...this.props.ForeignDisciplinesNotApplicable}
              name="ForeignDisciplinesNotApplicable"
              className="foreign-disciplines-not-applicable"
              onUpdate={this.updateForeignDisciplinesNotApplicable}
              onError={this.props.onError}
              or={i18n.m('foreign.pvq.spouseContacts.para.or')}
              label={i18n.t('foreign.pvq.spouseContacts.label.idk')}
              required={this.props.required}
              attribute={`${attribute}.ForeignDisciplinesNotApplicable`}
              includeNAConfirmation={Boolean(this.props.ForeignDisciplines?.value)}
            >
              <CheckboxGroup
                className="foreign-disciplines option-list option-list-vertical"
                onError={this.props.onError}
                required={this.props.required}
                selectedValues={this.props.ForeignDisciplines?.values}
                attribute={`${attribute}.ForeignDisciplines`}
              >
                {foreignNationalWorkOptions.map((option) => (
                  <Checkbox
                    key={option}
                    name={option}
                    label={option}
                    value={option}
                    className={option}
                    onUpdate={this.updateForeignDisciplines}
                    onError={this.props.onError}
                  />
                ))}
              </CheckboxGroup>
            </NotApplicable>
          </Field>

          {(this.props.ForeignDisciplines?.values?.includes('Other Government Agency')) && (
          <Field
            title={i18n.t('foreign.pvq.spouseContacts.heading.explanation')}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.ForeignDisciplinesOtherExplanation`}
          >
            <Textarea
              {...this.props.ForeignDisciplinesOtherExplanation}
              name="ForeignDisciplinesOtherExplanation"
              className="foreign-disciplines-other-explanation"
              onUpdate={this.updateForeignDisciplinesOtherExplanation}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.ForeignDisciplinesOtherExplanation`}
            />
          </Field>
          )}

          {(this.props.ForeignDisciplines?.values?.includes('Military or Armed Forces')
            || this.props.ForeignDisciplines?.values?.includes('Security Service')
            || this.props.ForeignDisciplines?.values?.includes('Intelligence Service')
            || this.props.ForeignDisciplines?.values?.includes('Defense Industry')
            || this.props.ForeignDisciplines?.values?.includes('Foreign Movement (such as a social, political, or religious movement)')) && (
            <Field
              title={i18n.t('foreign.pvq.spouseContacts.heading.foreignAssociationDescription')}
              titleSize="label"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.ForeignAssociationDescription`}
            >
              <Textarea
                {...this.props.ForeignAssociationDescription}
                name="ForeignAssociationDescription"
                className="foreign-disciplines-other-explanation"
                onUpdate={this.updateForeignAssociationDescription}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.ForeignAssociationDescription`}
              />
            </Field>
          )}

          <Field
            title={i18n.t('foreign.pvq.spouseContacts.heading.hasObligations')}
            attribute={`${attribute}.Obligations`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              {...this.props.Obligations}
              name={`${attribute}.Obligations`}
              attribute="Obligations"
              className="obligations"
              onUpdate={this.updateObligations}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        </>
        )}
      </div>
    );
  }
}

interface ForeignNationalNameItemProps extends AccordionItemProps, ForeignContactsItemAliasesModel {
  scrollIntoView?: CommonScrollIntoView
  required?: CommonRequired
  attribute?: string
  onUpdate?: CommonOnUpdate
  // Temporary. This will be removed in PI 11
  handleNameError: any
  formType?: string
}

const ForeignNationalNameItem: React.FC<ForeignNationalNameItemProps> = function ({
  scrollIntoView,
  required,
  attribute,
  handleNameError,
  $index,
  onUpdate,
  Alias,
  AliasDescription,
  AliasDescriptionOtherExplanation,
}) {
  const nameAttribute = `${attribute}.items[${$index}].Item.Alias`;
  const nameOnUpdate = (values: Partial<NameModel>) => {
    onUpdate({ Alias: values });
  };

  const aliasDescriptionOnUpdate = (values) => {
    onUpdate({
      AliasDescription: values,
      AliasDescriptionOtherExplanation: values.value === 'Other' ? (AliasDescriptionOtherExplanation || {}) : {}
    });
  };

  const aliasDescriptionOtherExplanationOnUpdate = (values) => {
    onUpdate({ AliasDescriptionOtherExplanation: values });
  };

  return (
    <>
      <Field
        title={i18n.t('foreign.pvq.spouseContacts.heading.aliasname')}
        optional
        filterErrors={Name.requiredErrorsOnly}
        scrollIntoView={scrollIntoView}
      >
        <PVQName
          {...Alias}
          name="Alias"
          onUpdate={nameOnUpdate}
          aria-label="Provide the other name or nickname"
          required={required}
          scrollIntoView={scrollIntoView}
          onError={handleNameError}
          attribute={nameAttribute}
          showFirstUnknown
          showMiddleUnknown
          showLastUnknown
        />
      </Field>

      <Field
        title={i18n.t('foreign.pvq.spouseContacts.heading.nameDescription')}
        filterErrors={Name.requiredErrorsOnly}
        className="foreign-contact-alias-description"
        scrollIntoView={scrollIntoView}
        attribute={`${attribute}.AliasDescription`}
      >
        <RadioGroup
          className="ordered-by option-list option-list-vertical"
          required={required}
          onError={handleNameError}
          selectedValue={AliasDescription?.value}
          attribute={`${attribute}.items[${$index}].Item.AliasDescription`}
        >
          {otherNamesOptions.map((option) => (
            <Radio
              key={option}
              name={option}
              label={option}
              value={option}
              className={option}
              onUpdate={aliasDescriptionOnUpdate}
              onError={handleNameError}
            />
          ))}
        </RadioGroup>
      </Field>

      {AliasDescription?.value === 'Other'
        && (
          <>
            {i18n.t('foreign.pvq.spouseContacts.label.explain')}
            <Textarea
              {...AliasDescriptionOtherExplanation}
              name="AliasDescriptionOtherExplanation"
              className="foreign-contact-alias-description-other"
              onUpdate={aliasDescriptionOtherExplanationOnUpdate}
              onError={handleNameError}
              required={required}
              attribute={`${attribute}.items[${$index}].Item.AliasDescriptionOtherExplanation`}
            />
          </>
        )}
    </>
  );
};

SpouseForeignNational.defaultProps = {
  Name: {},
  NameExplanation: {},
  FirstContact: {},
  LastContact: {},
  Methods: {},
  MethodsNotApplicable: { applicable: true },
  MethodsExplanation: {},
  Frequency: {},
  FrequencyExplanation: {},
  Relationship: {},
  RelationshipExplanation: {},
  Aliases: {},
  Citizenship: {},
  CitizenshipNotApplicable: { applicable: true },
  Birthdate: {},
  BirthdateNotApplicable: { applicable: true },
  Birthplace: {},
  BirthplaceNotApplicable: { applicable: true },
  Address: {},
  AddressNotApplicable: { applicable: true },
  Employer: {},
  EmployerNotApplicable: { applicable: true },
  EmployerAddress: {},
  EmployerAddressNotApplicable: { applicable: true },
  BirthplaceExplanation: {},
  OrganizationNotApplicable: { applicable: true },
  OrganizationAddressNotApplicable: { applicable: true },
  Affiliations: {},
  ListedPreviously: {},
  SpouseRelationship: {},
  SpouseRelationshipExplanation: {},
  ForeignDisciplines: {},
  ForeignDisciplinesOtherExplanation: {},
  ForeignDisciplinesNotApplicable: { applicable: true },
  ForeignAssociationDescription: {},
  ForeignAssociationDescriptionNotApplicable: { applicable: true },
  Obligations: {},
  addressBooks: {},
  dispatch: () => { },
  onUpdate: () => { },
  onError: (value, arr: ErrorCode[]) => arr,
};
